import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, Minus, PlugZap, RefreshCw, Trash2, Unplug } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Sheet, SheetBody, SheetContent, SheetFooter } from '@/components/ui/sheet';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { useConfirm } from '@/hooks/useConfirm';
import { cn } from '@/lib/utils';
import {
    useConnectCampaignFacebook,
    useDisconnectCampaignFacebook,
} from '@/api/campaigns/use-campaign-facebook';
import type { FacebookStatus } from '@/api/campaigns/types';
import type {
    ConnectionCheckResult,
    TenantIntegration,
} from '@/api/integrations/integrationService';
import {
    useCheckConnection,
    useDeleteIntegration,
    useToggleIntegration,
} from '@/api/integrations/use-integrations';
import FacebookSetupGuide from '../campaigns/FacebookSetupGuide';
import FacebookTestResult from '../campaigns/FacebookTestResult';
import FacebookPermissionList from '../campaigns/FacebookPermissionList';
import {
    FACEBOOK_PERMISSIONS,
    parseFacebookConnectError,
    parseFacebookExpiry,
} from '../campaigns/facebookPermissions';
import { getChannel, getProvider, type CatalogProvider } from './catalog';
import { DrawerHeading } from './DrawerHeading';
import { deriveFacebookStatus, formatRelativeTime } from './integrationHelpers';
import { ProviderFields } from './ProviderFields';
import { useMetaLine } from './useMetaLine';
import { useProviderForm } from './useProviderForm';

interface FacebookDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The saved `facebook` / `meta` integration, when credentials were saved. */
    row?: TenantIntegration;
    /** `GET /crm/campaigns/facebook/status`; undefined while loading or without campaigns access. */
    status?: FacebookStatus;
    canEdit: boolean;
    canCheck: boolean;
    /** CAMPAIGNS create / edit: connect, sync and disconnect. */
    canSync: boolean;
}

const Step: React.FC<{
    index: number;
    title: string;
    done: boolean;
    last?: boolean;
    children: React.ReactNode;
}> = ({ index, title, done, last, children }) => {
    const { t } = useTranslation('crm');
    return (
        <li className="flex gap-3">
            <div className="flex flex-col items-center">
                <span
                    className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold',
                        done
                            ? 'border-success/30 bg-success/10 text-success'
                            : 'border-border bg-muted text-muted-foreground'
                    )}
                >
                    {done ? <Check className="h-4 w-4" aria-hidden="true" /> : index}
                    {done && <span className="sr-only">{t('integrations.facebook.done')}</span>}
                </span>
                {!last && <span className="mt-1 w-px flex-1 bg-border" aria-hidden="true" />}
            </div>
            <div className={cn('min-w-0 flex-1 space-y-3', !last && 'pb-6')}>
                <h3 className="pt-1 text-sm font-semibold text-foreground">{title}</h3>
                {children}
            </div>
        </li>
    );
};

/**
 * Facebook Lead Ads drawer: a four-step checklist (app credentials, System User token,
 * permissions, pages), the setup guide, and Disconnect / Check token / Close.
 */
export const FacebookDrawer: React.FC<FacebookDrawerProps> = ({
    open,
    onOpenChange,
    row,
    status,
    canEdit,
    canCheck,
    canSync,
}) => {
    const { t, i18n } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();
    const describe = useMetaLine();
    const channel = getChannel('facebook');
    const provider = getProvider('facebook', 'meta') as CatalogProvider;

    const connect = useConnectCampaignFacebook();
    const disconnect = useDisconnectCampaignFacebook();
    const check = useCheckConnection();
    const toggle = useToggleIntegration();
    const remove = useDeleteIntegration();

    const { form, isSaving, generalError, save } = useProviderForm({
        type: 'facebook',
        provider,
        row,
    });
    const [checkResult, setCheckResult] = useState<ConnectionCheckResult | undefined>();
    const [syncIssues, setSyncIssues] = useState<{ errors: string[]; missing: string[] } | null>(
        null
    );
    const [syncWarnings, setSyncWarnings] = useState<string[]>([]);

    const credentialFields = {
        ...provider,
        fields: provider.fields.filter(field => field.key !== 'system_user_token'),
    };
    const tokenFields = {
        ...provider,
        fields: provider.fields.filter(field => field.key === 'system_user_token'),
    };

    const hasCredentials = !!row && !!row.configuration?.app_id && !!row.configuration?.app_secret;
    const hasToken = status?.has_token ?? !!row?.configuration?.system_user_token;
    const derived = deriveFacebookStatus(status, row);
    const meta = describe(derived);
    const dirty = form.formState.isDirty;

    const connection = status?.connection ?? null;
    const grantedScopes: string[] | null =
        checkResult?.granted_scopes ?? connection?.granted_scopes ?? null;
    const pages = status?.pages ?? [];
    const expiry = parseFacebookExpiry(connection?.token_expires_at);

    useEffect(() => {
        if (!open) {
            setCheckResult(undefined);
            setSyncIssues(null);
            setSyncWarnings([]);
        }
    }, [open]);

    const runCheck = async () => {
        if (!row) return;
        setCheckResult(undefined);
        try {
            const result = await check.mutateAsync(row.id);
            setCheckResult(result);
        } catch (error: any) {
            setCheckResult({
                valid: false,
                message: error?.response?.data?.message || t('integrations.verify.checkFailed'),
            });
        }
    };

    const sync = async () => {
        setSyncIssues(null);
        setSyncWarnings([]);
        try {
            const result = await connect.mutateAsync();
            setSyncWarnings(result.warnings ?? []);
            enqueueSnackbar(t('integrations.facebook.synced', { count: result.pages.length }), {
                variant: 'success',
            });
        } catch (error) {
            const { errors, missingPermissions } = parseFacebookConnectError(error);
            setSyncIssues({
                errors: errors.length > 0 ? errors : [t('integrations.facebook.syncFailed')],
                missing: missingPermissions,
            });
        }
    };

    const disconnectFacebook = async () => {
        const ok = await confirm({
            title: t('integrations.facebook.disconnectTitle'),
            description: t('integrations.facebook.disconnectDescription'),
            confirmLabel: t('integrations.facebook.disconnect'),
            variant: 'destructive',
        });
        if (!ok) return;
        try {
            await disconnect.mutateAsync();
            setCheckResult(undefined);
            enqueueSnackbar(t('integrations.facebook.disconnected'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('integrations.facebook.disconnectFailed'), { variant: 'error' });
        }
    };

    const removeCredentials = async () => {
        if (!row) return;
        const ok = await confirm({
            title: t('integrations.facebook.removeTitle'),
            description: t('integrations.facebook.removeDescription'),
            confirmLabel: t('integrations.actions.remove'),
            variant: 'destructive',
        });
        if (!ok) return;
        try {
            await remove.mutateAsync(row.id);
            enqueueSnackbar(t('integrations.toasts.removed', { name: provider.name }), {
                variant: 'success',
            });
        } catch {
            enqueueSnackbar(t('integrations.toasts.removeFailed'), { variant: 'error' });
        }
    };

    const enableRow = async () => {
        if (!row) return;
        try {
            await toggle.mutateAsync({ id: row.id, enabled: true });
        } catch {
            enqueueSnackbar(t('integrations.toasts.toggleFailed'), { variant: 'error' });
        }
    };

    const showSave = canEdit && (dirty || !row);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent closeLabel={t('integrations.actions.close')}>
                <DrawerHeading channel={channel} />

                <SheetBody className="space-y-6">
                    <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 p-3">
                        <div className="min-w-0 space-y-1">
                            <StatusBadge
                                kind="integration"
                                status={derived.state}
                                label={t(`integrations.status.${derived.state}`)}
                            />
                            <p className="text-xs text-muted-foreground">{meta.text}</p>
                        </div>
                    </div>

                    {row && !row.is_active && (
                        <Alert variant="warning">
                            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                                <span>{t('integrations.facebook.disabled')}</span>
                                {canEdit && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={enableRow}
                                        disabled={toggle.isPending}
                                    >
                                        {t('integrations.facebook.enable')}
                                    </Button>
                                )}
                            </AlertDescription>
                        </Alert>
                    )}

                    <FacebookSetupGuide />

                    {generalError && (
                        <Alert variant="destructive" role="alert">
                            <AlertDescription>{generalError}</AlertDescription>
                        </Alert>
                    )}

                    <Form {...form}>
                        <form
                            noValidate
                            onSubmit={event => {
                                event.preventDefault();
                                void save(row ? undefined : true);
                            }}
                        >
                            <ol className="list-none">
                                <Step
                                    index={1}
                                    title={t('integrations.facebook.steps.credentials')}
                                    done={hasCredentials}
                                >
                                    <p className="text-sm text-muted-foreground">
                                        {t('integrations.facebook.steps.credentialsHint')}
                                    </p>
                                    <ProviderFields
                                        form={form}
                                        provider={credentialFields}
                                        saved={row?.configuration}
                                        disabled={!canEdit}
                                    />
                                    {canEdit && row && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            className="text-destructive hover:text-destructive"
                                            onClick={removeCredentials}
                                            disabled={remove.isPending}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                            {t('integrations.facebook.removeCredentials')}
                                        </Button>
                                    )}
                                </Step>

                                <Step
                                    index={2}
                                    title={t('integrations.facebook.steps.token')}
                                    done={hasToken}
                                >
                                    <p className="text-sm text-muted-foreground">
                                        {t('integrations.facebook.steps.tokenHint')}
                                    </p>
                                    <ProviderFields
                                        form={form}
                                        provider={tokenFields}
                                        saved={row?.configuration}
                                        disabled={!canEdit}
                                    />
                                    {connection && (
                                        <p className="text-xs text-muted-foreground">
                                            {t('integrations.facebook.connectedAs', {
                                                name: connection.fb_user_name,
                                            })}
                                            {connection.token_type
                                                ? ` · ${connection.token_type}`
                                                : ''}
                                            {' · '}
                                            {expiry
                                                ? t('integrations.facebook.expires', {
                                                      when:
                                                          formatRelativeTime(
                                                              expiry.toISOString(),
                                                              i18n.language
                                                          ) ?? '',
                                                  })
                                                : t('campaigns.facebookSetup.test.neverExpires')}
                                        </p>
                                    )}
                                </Step>

                                <Step
                                    index={3}
                                    title={t('integrations.facebook.steps.permissions')}
                                    done={
                                        grantedScopes !== null &&
                                        FACEBOOK_PERMISSIONS.filter(
                                            item => item.level === 'required'
                                        ).every(item => grantedScopes.includes(item.name))
                                    }
                                >
                                    <ul className="divide-y divide-border rounded-lg border border-border">
                                        {FACEBOOK_PERMISSIONS.map(permission => {
                                            const granted = grantedScopes?.includes(
                                                permission.name
                                            );
                                            const missingRequired =
                                                grantedScopes !== null &&
                                                !granted &&
                                                permission.level === 'required';
                                            return (
                                                <li
                                                    key={permission.name}
                                                    className="flex items-start gap-3 px-3 py-2"
                                                >
                                                    <span
                                                        className={cn(
                                                            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                                                            granted
                                                                ? 'bg-success/10 text-success'
                                                                : missingRequired
                                                                  ? 'bg-destructive/10 text-destructive'
                                                                  : 'bg-muted text-muted-foreground'
                                                        )}
                                                    >
                                                        {granted ? (
                                                            <Check className="h-3.5 w-3.5" />
                                                        ) : (
                                                            <Minus className="h-3.5 w-3.5" />
                                                        )}
                                                        <span className="sr-only">
                                                            {granted
                                                                ? t('integrations.facebook.granted')
                                                                : t(
                                                                      'integrations.facebook.notGranted'
                                                                  )}
                                                        </span>
                                                    </span>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <code className="rounded bg-muted px-1 py-0.5 text-xs">
                                                                {permission.name}
                                                            </code>
                                                            <span className="text-xs text-muted-foreground">
                                                                {t(
                                                                    `campaigns.facebookSetup.levels.${permission.level}`
                                                                )}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-muted-foreground">
                                                            {t(permission.descriptionKey)}
                                                        </p>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    {grantedScopes === null && (
                                        <p className="text-xs text-muted-foreground">
                                            {t('integrations.facebook.permissionsUnknown')}
                                        </p>
                                    )}
                                </Step>

                                <Step
                                    index={4}
                                    title={t('integrations.facebook.steps.pages')}
                                    done={pages.length > 0}
                                    last
                                >
                                    {pages.length > 0 ? (
                                        <ul className="divide-y divide-border rounded-lg border border-border">
                                            {pages.map(page => (
                                                <li
                                                    key={page.id}
                                                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                                                >
                                                    <span className="truncate font-medium">
                                                        {page.name}
                                                    </span>
                                                    <span
                                                        dir="ltr"
                                                        className="shrink-0 text-xs text-muted-foreground"
                                                    >
                                                        {page.page_id}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">
                                            {t('integrations.facebook.noPages')}
                                        </p>
                                    )}

                                    {canSync && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={sync}
                                            disabled={!hasToken || connect.isPending || dirty}
                                        >
                                            {connect.isPending ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <RefreshCw className="h-4 w-4" />
                                            )}
                                            {t('integrations.facebook.syncPages')}
                                        </Button>
                                    )}

                                    {syncIssues && (
                                        <Alert variant="destructive" role="alert">
                                            <AlertDescription className="space-y-2">
                                                {syncIssues.errors.map(message => (
                                                    <p key={message}>{message}</p>
                                                ))}
                                                {syncIssues.missing.length > 0 && (
                                                    <FacebookPermissionList
                                                        permissions={syncIssues.missing}
                                                    />
                                                )}
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    {syncWarnings.length > 0 && (
                                        <Alert variant="warning">
                                            <AlertDescription className="space-y-1">
                                                {syncWarnings.map(message => (
                                                    <p key={message}>{message}</p>
                                                ))}
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                </Step>
                            </ol>
                        </form>
                    </Form>

                    {checkResult && <FacebookTestResult result={checkResult} />}

                    <p className="text-sm text-muted-foreground">
                        {t('integrations.facebook.leadSync')}{' '}
                        <Link to="/campaigns" className="font-medium text-primary hover:underline">
                            {t('integrations.facebook.openCampaigns')}
                        </Link>
                    </p>
                </SheetBody>

                <SheetFooter>
                    {canSync && connection && (
                        <Button
                            type="button"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={disconnectFacebook}
                            disabled={disconnect.isPending}
                        >
                            <Unplug className="h-4 w-4" />
                            {t('integrations.facebook.disconnect')}
                        </Button>
                    )}
                    <div className="ms-auto flex items-center gap-2">
                        {showSave && (
                            <Button
                                type="button"
                                onClick={() => void save(row ? undefined : true)}
                                disabled={isSaving}
                            >
                                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                                {t('actions.save')}
                            </Button>
                        )}
                        {canCheck && row && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={runCheck}
                                disabled={!hasToken || dirty || check.isPending}
                            >
                                {check.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <PlugZap className="h-4 w-4" />
                                )}
                                {t('integrations.facebook.checkToken')}
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant={showSave ? 'outline' : 'default'}
                            onClick={() => onOpenChange(false)}
                        >
                            {t('integrations.actions.close')}
                        </Button>
                    </div>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};

export default FacebookDrawer;
